import type { PoolTokenPermissionsResponse, TokenPermission } from "@auto-pools/core";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api/client";
import { shorten } from "../lib/format";
import { TokenIcon } from "./TokenPair";

const EDGE = 8;

type LoadState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ready"; response: PoolTokenPermissionsResponse };

const permissionCopy: Record<
  TokenPermission["kind"],
  { title: string; active: string; inactive: string; href: string }
> = {
  "freeze-authority": {
    title: "Freeze authority",
    active: "This authority can freeze token accounts, preventing them from transferring tokens.",
    inactive: "The freeze authority is not currently active.",
    href: "https://solana.com/docs/tokens/basics/set-authority#freeze-account-authority",
  },
  "mutable-metadata": {
    title: "Mutable metadata",
    active:
      "This authority can change the token’s on-chain name, symbol, image, and other metadata.",
    inactive: "The token metadata is currently immutable.",
    href: "https://solana.com/docs/tokens/extensions/metadata",
  },
  "transfer-hook": {
    title: "Transfer hook",
    active: "A custom program currently runs when this token is transferred.",
    inactive:
      "The transfer hook is currently disabled, but the retained authority can enable a hook later.",
    href: "https://solana.com/docs/tokens/extensions/transfer-hook",
  },
  "permanent-delegate": {
    title: "Permanent delegate",
    active: "This delegate can transfer or burn tokens from any holder’s token account.",
    inactive: "The permanent delegate is not currently active.",
    href: "https://solana.com/docs/tokens/extensions/permanent-delegate",
  },
  pausable: {
    title: "Pausable transfers",
    active: "Token transfers are currently paused by the pause authority.",
    inactive: "Transfers are currently unpaused, but this authority can pause them later.",
    href: "https://solana.com/docs/tokens/extensions/pausable",
  },
};

export function TokenPermissions({
  poolAddress,
  onResponse,
  knownResponse,
}: {
  poolAddress: string;
  onResponse?: (response: PoolTokenPermissionsResponse | null) => void;
  knownResponse?: PoolTokenPermissionsResponse;
}) {
  const [state, setState] = useState<LoadState>(
    knownResponse ? { status: "ready", response: knownResponse } : { status: "loading" },
  );
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: EDGE, top: EDGE });
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialogId = useId();
  const titleId = useId();

  useEffect(() => {
    if (knownResponse) {
      setState(
        knownResponse.poolAddress === poolAddress
          ? { status: "ready", response: knownResponse }
          : { status: "failed" },
      );
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    setOpen(false);
    onResponse?.(null);
    void api<PoolTokenPermissionsResponse>(
      `/pools/${encodeURIComponent(poolAddress)}/token-permissions`,
      { signal: controller.signal, sensitive: true },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.poolAddress !== poolAddress) {
          setState({ status: "failed" });
          return;
        }
        setState({ status: "ready", response });
        onResponse?.(response);
      })
      .catch((reason: unknown) => {
        if (
          controller.signal.aborted ||
          (reason instanceof DOMException && reason.name === "AbortError")
        )
          return;
        setState({ status: "failed" });
      });
    return () => {
      controller.abort();
    };
  }, [knownResponse, onResponse, poolAddress, retry]);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) requestAnimationFrame(() => trigger.current?.focus());
  }, []);
  const place = useCallback(() => {
    const anchor = trigger.current;
    const panel = popup.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const left = Math.min(Math.max(EDGE, a.left), Math.max(EDGE, innerWidth - p.width - EDGE));
    const below = a.bottom + 6;
    const top =
      below + p.height <= innerHeight - EDGE ? below : Math.max(EDGE, a.top - p.height - 6);
    setPosition({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    closeButton.current?.focus({ preventScroll: true });
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popup.current?.contains(target) && !trigger.current?.contains(target)) close(true);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close(true);
    };
    window.addEventListener("pointerdown", outside);
    window.addEventListener("keydown", key, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("pointerdown", outside);
      window.removeEventListener("keydown", key, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [close, open, place]);

  if (state.status === "loading")
    return <span className="token-permissions-status">Checking permissions…</span>;
  if (state.status === "failed")
    return (
      <span className="token-permissions-status warning">
        Permissions unavailable ·{" "}
        <button
          type="button"
          className="link"
          onClick={() => {
            setRetry((value) => value + 1);
          }}
        >
          Retry
        </button>
      </span>
    );

  const rows = state.response.tokens.flatMap((token) =>
    token.permissions.map((permission) => ({ token, permission })),
  );
  if (rows.length === 0) {
    const blocks = state.response.tokens.filter((token) => token.transferBlockedReason !== null);
    if (blocks.length === 0) return null;
    return (
      <span className="token-permissions-status warning" role="alert">
        {blocks
          .map((token) =>
            token.transferBlockedReason === null
              ? ""
              : `${token.symbol}: ${token.transferBlockedReason}`,
          )
          .join(" · ")}
      </span>
    );
  }
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="token-permissions-badge"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <svg viewBox="0 0 16 18" aria-hidden="true">
          <path d="M8 1 14 3.5v4.7c0 4-2.5 6.8-6 8.3-3.5-1.5-6-4.3-6-8.3V3.5L8 1Z" />
        </svg>
        Permissions
      </button>
      {open &&
        createPortal(
          <div
            ref={popup}
            id={dialogId}
            className="token-permissions-popup"
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            style={position}
          >
            <div className="pool-details-heading">
              <strong id={titleId}>Token Permissions</strong>
              <button
                ref={closeButton}
                type="button"
                onClick={() => {
                  close(true);
                }}
              >
                Close
              </button>
            </div>
            {state.response.tokens.some((token) => token.transferBlockedReason !== null) && (
              <div className="token-transfer-blocks">
                {state.response.tokens.map(
                  (token) =>
                    token.transferBlockedReason && (
                      <p key={token.mint} className="inline-alert warning">
                        <strong>{token.symbol}:</strong> {token.transferBlockedReason}
                      </p>
                    ),
                )}
              </div>
            )}
            <div className="token-permission-list">
              {rows.map(({ token, permission }) => {
                const copy = permissionCopy[permission.kind];
                return (
                  <section
                    key={`${token.mint}:${permission.kind}:${permission.authority}`}
                    className="token-permission-row"
                  >
                    <TokenIcon token={token} />
                    <div>
                      <h3>
                        {token.symbol} · {copy.title}
                      </h3>
                      <p>{permission.active ? copy.active : copy.inactive}</p>
                      <small title={permission.authority}>
                        Authority {shorten(permission.authority, 6)} ·{" "}
                        <a href={copy.href} target="_blank" rel="noopener noreferrer">
                          Learn more ↗
                        </a>
                      </small>
                    </div>
                  </section>
                );
              })}
            </div>
            <small>
              Observed {new Date(state.response.observedAt).toLocaleString()} · pool metadata only
            </small>
          </div>,
          trigger.current?.closest("dialog") ?? document.body,
        )}
    </>
  );
}
